// Database smart prompt entries — mirrors the database-typed entries in
// @confluence/smart-create confluenceObjectExamples.tsx in the Confluence monorepo.
// Source of truth: confluence/next/packages/smart-create/src/confluenceObjectExamples.tsx
// Keep in sync when prompts are updated there.

import { defineMessages } from 'react-intl-next';

import calendarIcon from '../assets/smart-prompts/smart-prompt-calendar.svg';
import checklistIcon from '../assets/smart-prompts/smart-prompt-checklist.svg';
import customerIcon from '../assets/smart-prompts/smart-prompt-customer.svg';
import flowchartIcon from '../assets/smart-prompts/smart-prompt-flowchart.svg';
import guidelinesIcon from '../assets/smart-prompts/smart-prompt-guidelines.svg';
import iconsPeopleIcon from '../assets/smart-prompts/smart-prompt-icons-people.svg';
import lightbulbIcon from '../assets/smart-prompts/smart-prompt-lightbulb.svg';
import marketingIcon from '../assets/smart-prompts/smart-prompt-marketing.svg';
import projectManagementIcon from '../assets/smart-prompts/smart-prompt-project-management.svg';
import resilienceIcon from '../assets/smart-prompts/smart-prompt-resilience.svg';
import trustIcon from '../assets/smart-prompts/smart-prompt-trust.svg';
import userTestingIcon from '../assets/smart-prompts/smart-prompt-user-testing.svg';
import volunteeringIcon from '../assets/smart-prompts/smart-prompt-volunteering.svg';

export const databaseSmartPromptMessages = defineMessages({
  productRequirements: {
    id: 'database-smart-prompts.product-requirements',
    defaultMessage: 'Track product requirements',
    description: 'Smart prompt label for creating a product requirements database'
  },
  architectureDecisionLog: {
    id: 'database-smart-prompts.architecture-decision-log',
    defaultMessage: 'Architecture decision log',
    description: 'Smart prompt label for creating an architecture decision log database'
  },
  contentLibrary: {
    id: 'database-smart-prompts.content-library',
    defaultMessage: 'Content library',
    description: 'Smart prompt label for creating a content library database'
  },
  accountTracker: {
    id: 'database-smart-prompts.account-tracker',
    defaultMessage: 'Account tracker',
    description: 'Smart prompt label for creating an account tracker database'
  },
  projectPortfolioDashboard: {
    id: 'database-smart-prompts.project-portfolio-dashboard',
    defaultMessage: 'Project portfolio dashboard',
    description: 'Smart prompt label for creating a project portfolio dashboard database'
  },
  onboardingChecklist: {
    id: 'database-smart-prompts.onboarding-checklist',
    defaultMessage: 'Onboarding checklist',
    description: 'Smart prompt label for creating an onboarding checklist database'
  },
  stakeholderMap: {
    id: 'database-smart-prompts.stakeholder-map',
    defaultMessage: 'Stakeholder map',
    description: 'Smart prompt label for creating a stakeholder map database'
  },
  customerFeedbackLog: {
    id: 'database-smart-prompts.customer-feedback-log',
    defaultMessage: 'Customer feedback log',
    description: 'Smart prompt label for creating a customer feedback log database'
  },
  incidentTracker: {
    id: 'database-smart-prompts.incident-tracker',
    defaultMessage: 'Incident tracker',
    description: 'Smart prompt label for creating an incident tracker database'
  },
  engineeringCapacityPlanner: {
    id: 'database-smart-prompts.engineering-capacity-planner',
    defaultMessage: 'Engineering capacity planner',
    description:
      'Smart prompt label for creating an engineering capacity planner database'
  },
  pressCoverageLog: {
    id: 'database-smart-prompts.press-coverage-log',
    defaultMessage: 'Press coverage log',
    description: 'Smart prompt label for creating a press coverage log database'
  },
  campaignTracker: {
    id: 'database-smart-prompts.campaign-tracker',
    defaultMessage: 'Campaign tracker',
    description: 'Smart prompt label for creating a campaign tracker database'
  },
  salesEnablementLibrary: {
    id: 'database-smart-prompts.sales-enablement-library',
    defaultMessage: 'Sales enablement library',
    description: 'Smart prompt label for creating a sales enablement library database'
  },
  riskRegister: {
    id: 'database-smart-prompts.risk-register',
    defaultMessage: 'Risk register',
    description: 'Smart prompt label for creating a risk register database'
  },
  okrTracker: {
    id: 'database-smart-prompts.okr-tracker',
    defaultMessage: 'OKR tracker',
    description: 'Smart prompt label for creating an OKR tracker database'
  },
  actionItemTracker: {
    id: 'database-smart-prompts.action-item-tracker',
    defaultMessage: 'Action item tracker',
    description: 'Smart prompt label for creating an action item tracker database'
  },
  resourceDirectory: {
    id: 'database-smart-prompts.resource-directory',
    defaultMessage: 'Resource directory',
    description: 'Smart prompt label for creating a resource directory database'
  }
});

export type DatabaseSmartPromptEntry = {
  name: string;
  prompt: string;
  templateName: (typeof databaseSmartPromptMessages)[keyof typeof databaseSmartPromptMessages];
  icon: string;
  userRole: string;
  cardLevel: 'primary' | 'secondary';
};

export const DATABASE_SMART_PROMPT_ENTRIES: DatabaseSmartPromptEntry[] = [
  // Primary entries (one per role)
  {
    name: 'productManagement1',
    prompt:
      'Create a database to capture product requirements for [project or feature], including user stories, acceptance criteria, and priority',
    templateName: databaseSmartPromptMessages.productRequirements,
    icon: lightbulbIcon,
    userRole: 'productManagement',
    cardLevel: 'primary'
  },
  {
    name: 'engineering2',
    prompt:
      'Build a database to capture key technical decisions, rationale, and outcomes for [project or feature].',
    templateName: databaseSmartPromptMessages.architectureDecisionLog,
    icon: trustIcon,
    userRole: 'engineering',
    cardLevel: 'primary'
  },
  {
    name: 'marketing1',
    prompt:
      'Set up a content library database for [project or launch] to organize marketing assets and blog posts with fields for asset type, target audience, status, and tags.',
    templateName: databaseSmartPromptMessages.contentLibrary,
    icon: flowchartIcon,
    userRole: 'marketing',
    cardLevel: 'primary'
  },
  {
    name: 'sales3',
    prompt:
      'Create a database to manage sales accounts for [product or feature] including fields for account name, deal stage, contract value, next steps and owner',
    templateName: databaseSmartPromptMessages.accountTracker,
    icon: volunteeringIcon,
    userRole: 'sales',
    cardLevel: 'primary'
  },
  {
    name: 'programManagement2',
    prompt:
      'Create a project portfolio database for [team or program] to track all active projects with fields for owner, status, key milestones and target dates',
    templateName: databaseSmartPromptMessages.projectPortfolioDashboard,
    icon: resilienceIcon,
    userRole: 'programManagement',
    cardLevel: 'primary'
  },
  {
    name: 'other3',
    prompt:
      'Build an onboarding checklist database for new hires, including tasks, due dates and completion status',
    templateName: databaseSmartPromptMessages.onboardingChecklist,
    icon: userTestingIcon,
    userRole: 'other',
    cardLevel: 'primary'
  },
  // Secondary entries
  {
    name: 'productManagerDatabase1',
    prompt:
      'Build a dynamic list of key stakeholders for [project or feature], including roles, influence, and engagement status.',
    templateName: databaseSmartPromptMessages.stakeholderMap,
    icon: iconsPeopleIcon,
    userRole: 'productManagement',
    cardLevel: 'secondary'
  },
  {
    name: 'productManagerDatabase2',
    prompt:
      'Create a feedback repository to collect, categorize, and prioritize user feedback for [project or feature].',
    templateName: databaseSmartPromptMessages.customerFeedbackLog,
    icon: customerIcon,
    userRole: 'productManagement',
    cardLevel: 'secondary'
  },
  {
    name: 'engineeringDatabase1',
    prompt:
      'Log incidents, root causes, resolutions, and follow up actions for [project or feature] in a structured table.',
    templateName: databaseSmartPromptMessages.incidentTracker,
    icon: resilienceIcon,
    userRole: 'engineering',
    cardLevel: 'secondary'
  },
  {
    name: 'engineeringDatabase2',
    prompt:
      'Build a planner for [team] to track capacity, allocations, and upcoming leave for better sprint planning.',
    templateName: databaseSmartPromptMessages.engineeringCapacityPlanner,
    icon: calendarIcon,
    userRole: 'engineering',
    cardLevel: 'secondary'
  },
  {
    name: 'marketingDatabase1',
    prompt:
      'Track media mentions, press releases, and coverage details for [project or launch] in a structured table.',
    templateName: databaseSmartPromptMessages.pressCoverageLog,
    icon: marketingIcon,
    userRole: 'marketing',
    cardLevel: 'secondary'
  },
  {
    name: 'marketingDatabase2',
    prompt:
      'Organize a table to plan, execute, and monitor marketing campaigns, including channels, owners, and OKRs.',
    templateName: databaseSmartPromptMessages.campaignTracker,
    icon: projectManagementIcon,
    userRole: 'marketing',
    cardLevel: 'secondary'
  },
  {
    name: 'salesDatabase1',
    prompt:
      'Organize a repository of sales playbooks, scripts, demos, and product guides for [feature or product].',
    templateName: databaseSmartPromptMessages.salesEnablementLibrary,
    icon: guidelinesIcon,
    userRole: 'sales',
    cardLevel: 'secondary'
  },
  {
    name: 'programManagementDatabase1',
    prompt:
      'Set up a risk log to identify, assess, and monitor risks across [program(s)].',
    templateName: databaseSmartPromptMessages.riskRegister,
    icon: resilienceIcon,
    userRole: 'programManagement',
    cardLevel: 'secondary'
  },
  {
    name: 'programManagementDatabase2',
    prompt:
      'Create a tracker to align objectives, key results, and progress updates for [team or program].',
    templateName: databaseSmartPromptMessages.okrTracker,
    icon: flowchartIcon,
    userRole: 'programManagement',
    cardLevel: 'secondary'
  },
  {
    name: 'otherDatabase1',
    prompt:
      'Create a database to track action items from recurring meetings, with fields for owner, due date, status, and the meeting where it originated',
    templateName: databaseSmartPromptMessages.actionItemTracker,
    icon: checklistIcon,
    userRole: 'other',
    cardLevel: 'secondary'
  },
  {
    name: 'otherDatabase2',
    prompt:
      'Build a team resource directory database listing key contacts, their roles, areas of expertise, and preferred communication channels',
    templateName: databaseSmartPromptMessages.resourceDirectory,
    icon: iconsPeopleIcon,
    userRole: 'other',
    cardLevel: 'secondary'
  }
];
